/* eslint-disable no-shadow */
import React, { useState, useEffect, useMemo } from 'react';
import { connect, useSelector } from 'react-redux';
import styled from 'styled-components';
import classNames from 'classnames';

import { ControllerStyles } from '../../../components/ControllerStyles';
import { Knob } from '../../../components/Knob';
import { Slider } from '../../../components/Slider';
import {
   addMixerTrackEffect,
   toggleMixerTrackMute,
   setMixerTrackGain,
   setMixerTrackPan,
   setView,
   toggleMixerTrackSolo,
} from '../actions';
import { sequencerViewTypes } from '../constants';
import { mixer } from '../mixer';
import { controllerValuesEqual } from '../../../util/controllerValuesEqual';
import { useCanvasSize } from '../../../hooks/useCanvasSize';
import { LedOnButton } from '../../../components/LedOnButton';
import { getHighlightedClassName } from '../../../util/getHighlightedClassName';

const mapStateToProps = ({
   activeInterface: {
      instrumentRack,
      loopModeReference,
      loopMode,
      loopModePlaying,
      compareAnswer,
      highlightedControls,
      suggestedValues,
      soloTrack,
   },
}) => ({
   instrumentRack,
   // probably will break this out, since I think i will need compare answer independently too
   // animateLevels: !(loopMode && loopModeReference && !compareAnswer),
   // displayReference:  && compareAnswer,
   compareAnswer,
   loopModePlaying,
   referenceMixer: loopMode && loopModeReference,
   highlightedControls,
   suggestedValues,
   soloTrack,
});

const mapDispatchToProps = {
   setView,
   addMixerTrackEffect,
   setMixerTrackGain,
   setMixerTrackPan,
   toggleMixerTrackMute,
   toggleMixerTrackSolo,
};

const MixerWrapper = styled.div`
   overflow: auto;
   overscroll-behavior-x: contain;
   height: var(--sequencer-zone-height);
   max-height: 500px;

   @media (min-width: 1150px) {
      min-width: 500px;
   }
`;

const MixerStyles = styled.svg`
   display: block;
   margin: 0 auto;
   overflow: hidden;
   touch-action: pan-x;
   -webkit-tap-highlight-color: transparent;
   .track-name {
      fill: var(--text-color);
      font-size: 14px;
      font-weight: bold;
      font-family: var(--content-font);
      opacity: 0.75;

      writing-mode: vertical-lr;
      transform: rotate(180deg);

      transform-box: fill-box;
      transform-origin: top center;
   }

   .text-button {
      fill: var(--text-color);
      font-size: 14px;
      font-weight: bold;
      font-family: var(--content-font);
   }

   .icon-button {
      fill: var(--text-color);
   }

   .effects-button {
      font-size: 20px;
   }

   &.greyscale {
      .track-name,
      .text-button,
      .icon-button {
         fill: #535353 !important;
      }
   }

   @media (prefers-color-scheme: dark) {
      &.greyscale {
         .track-name,
         .text-button,
         .icon-button {
            fill: #b3b3b3 !important;
         }
      }
   }
`;

const LevelsDisplayStyles = styled.g`
   &.levels-transparent .level-indicator {
      opacity: 0.33;
   }

   rect {
      fill: var(--brand-green);
      fill: var(--brand-light-green);
      fill: var(--text-color);
   }

   .level-background {
      /* fill: var(--brand-green); */
      fill: var(--brand-light-green);
      /* opacity: 0.25; */
      fill: none;
      stroke: var(--text-color);
      stroke-width: 2px;
      /* fill: var(--text-color); */
   }

   &.highlighted-left {
      .level-indicator.left-channel {
         fill: var(--brand-light-orange);
      }
      .level-indicator.right-channel {
         opacity: 0.33;
      }
      .level-background {
         stroke: var(--brand-light-orange);
      }
   }

   &.highlighted-right {
      .level-indicator.right-channel {
         fill: var(--brand-light-orange);
      }
      .level-indicator.left-channel {
         opacity: 0.33;
      }
      .level-background {
         stroke: var(--brand-light-orange);
      }
   }

   &.highlighted-still {
      .level-indicator {
         fill: var(--brand-light-orange);
      }
      .level-background {
         stroke: var(--brand-light-orange);
      }
   }

   &.greyscale {
      .level-indicator {
         fill: #535353 !important;
      }
      .level-background {
         stroke: #535353 !important;
      }
   }

   @media (prefers-color-scheme: dark) {
      &.greyscale {
         .level-indicator {
            fill: #b3b3b3 !important;
         }
         .level-background {
            stroke: #b3b3b3 !important;
         }
      }
   }
`;

const LevelsDisplay = ({
   animate: shouldAnimate,
   displayReference,
   mixerSizes,
   greyscale,
   playing,
   highlightedClass,
}) => {
   const [analyzerValues, setAnalyzerValues] = useState([]);
   const [transparentLevels, setTransparentLevels] = useState(true);

   const {
      trackWidth,
      trackGap,
      svgPadding,
      levelIndicatorHeight,
      levelIndicatorWidth,
   } = mixerSizes;

   // Use useRef for mutable variables that we want to persist
   // without triggering a re-render on their change
   const animateRef = React.useRef();
   const shouldAnimateRef = React.useRef();
   const displayReferenceRef = React.useRef();
   const analyzerRef = React.useRef();
   const playingRef = React.useRef();

   const animate = () => {
      if (shouldAnimateRef.current) {
         const newAnalyzerValues = mixer.tracks.map(
            ({ track, referenceTrack }, i) => {
               let prevL = 0;
               let prevR = 0;
               if (analyzerRef.current?.[i] != null) {
                  prevL = analyzerRef.current[i].l;
                  prevR = analyzerRef.current[i].r;
               }

               const leftArr = new Float32Array(track.analyzerL.fftSize);
               const rightArr = new Float32Array(track.analyzerR.fftSize);

               if (displayReferenceRef.current) {
                  referenceTrack.analyzerL.getFloatTimeDomainData(leftArr);
                  referenceTrack.analyzerR.getFloatTimeDomainData(rightArr);
               } else {
                  track.analyzerL.getFloatTimeDomainData(leftArr);
                  track.analyzerR.getFloatTimeDomainData(rightArr);
               }

               return {
                  l: Math.max(
                     prevL - 0.04,
                     Math.log10(Math.max(...leftArr.map(Math.abs)) * 9 + 1)
                  ),
                  r: Math.max(
                     prevR - 0.04,
                     Math.log10(Math.max(...rightArr.map(Math.abs)) * 9 + 1)
                  ),
               };
            }
         );
         setAnalyzerValues(newAnalyzerValues);
         analyzerRef.current = newAnalyzerValues;
         animateRef.current = requestAnimationFrame(animate);
      } else {
         const newAnalyzerValues = mixer.tracks.map((m, i) => {
            if (!playingRef.current) {
               return {
                  l: 0,
                  r: 0,
               };
            }
            const now = Date.now();

            const level = (Math.sin((now / 2000) * Math.PI - i) + 1) / 2;

            return {
               l: level,
               r: level,
            };
         });
         setAnalyzerValues(newAnalyzerValues);
         animateRef.current = requestAnimationFrame(animate);
      }
   };

   React.useEffect(() => {
      animateRef.current = requestAnimationFrame(animate);
      return () => cancelAnimationFrame(animateRef.current);
   }, []);

   React.useEffect(() => {
      shouldAnimateRef.current = shouldAnimate;
   }, [shouldAnimate]);

   React.useEffect(() => {
      playingRef.current = playing;
   }, [playing]);

   React.useEffect(() => {
      displayReferenceRef.current = displayReference;
   }, [displayReference]);

   React.useEffect(() => {
      const transparent = !shouldAnimate || displayReference;
      if (!transparent) {
         setTimeout(() => setTransparentLevels(transparent), 10);
         return;
      }
      setTransparentLevels(transparent);
   }, [!shouldAnimate || displayReference]);

   return (
      <LevelsDisplayStyles
         className={classNames({
            'levels-transparent': transparentLevels,

            greyscale,
            [highlightedClass]: highlightedClass != null,
         })}
      >
         {analyzerValues.map(({ l, r }, instrumentIndex) => (
            <g key={instrumentIndex}>
               <rect
                  className="level-background"
                  height={levelIndicatorHeight}
                  width={levelIndicatorWidth}
                  x={
                     svgPadding + 20 + (trackWidth + trackGap) * instrumentIndex
                  }
                  y={0 + svgPadding}
                  rx={2}
               />
               <rect
                  className="level-indicator left-channel"
                  height={levelIndicatorHeight * l}
                  width={levelIndicatorWidth / 2}
                  x={
                     svgPadding + 20 + (trackWidth + trackGap) * instrumentIndex
                  }
                  y={
                     levelIndicatorHeight -
                     levelIndicatorHeight * l +
                     svgPadding
                  }
               />
               <rect
                  className="level-indicator right-channel"
                  height={levelIndicatorHeight * r}
                  width={levelIndicatorWidth / 2}
                  x={
                     svgPadding +
                     20 +
                     levelIndicatorWidth / 2 +
                     (trackWidth + trackGap) * instrumentIndex
                  }
                  y={
                     levelIndicatorHeight -
                     levelIndicatorHeight * r +
                     svgPadding
                  }
               />
            </g>
         ))}
      </LevelsDisplayStyles>
   );
};

const levelIndicatorPositions = { adjacent: 'adjacent', above: 'above' };

const FullMixer = connect(
   mapStateToProps,
   mapDispatchToProps
)(
   ({
      instrumentRack,
      compareAnswer,
      referenceMixer,
      soloTrack,
      setView,
      // addMixerTrackEffect,
      setMixerTrackGain,
      setMixerTrackPan,
      highlightedControls,
      suggestedValues,
      loopModePlaying,
      toggleMixerTrackMute,
      toggleMixerTrackSolo,
   }) => {
      const { width: canvasWidth, height: canvasHeight } = useCanvasSize(
         'mixer-canvas',
         'sequencer-interface'
      );

      const instrumentCount = instrumentRack.length;

      const mixerSizes = useMemo(() => {
         const levelIndicatorPosition =
            canvasHeight > 300
               ? levelIndicatorPositions.above
               : levelIndicatorPositions.adjacent;

         // Regular (above) Layout defaults
         let controlWidth = 40;
         let levelAndKnobSectionWidth = controlWidth;
         let sliderSectionWidth = controlWidth;
         let buttonSectionWidth = controlWidth;
         const trackInnerGap = 4;
         let trackWidth =
            sliderSectionWidth + buttonSectionWidth + trackInnerGap;
         let trackGap = 20;
         let panKnobPadding = 3;
         const svgPadding = 6;
         let verticalElementPadding = 15;
         let levelIndicatorHeight = 120;
         let levelIndicatorWidth = levelAndKnobSectionWidth - 20;

         // adjacent overrides based on canvas size
         if (levelIndicatorPosition === levelIndicatorPositions.adjacent) {
            const visibleTracks = Math.min(instrumentCount, 8);
            const requiredTrackWidthInclcudingGap = canvasWidth / visibleTracks;

            if (requiredTrackWidthInclcudingGap < (105 * 3) / 2) {
               sliderSectionWidth = 35;
            }

            if (requiredTrackWidthInclcudingGap < (100 * 3) / 2) {
               sliderSectionWidth = 30;
            }

            if (requiredTrackWidthInclcudingGap < (95 * 3) / 2) {
               trackGap = 15;
               buttonSectionWidth = 35;
            }

            if (requiredTrackWidthInclcudingGap < (90 * 3) / 2) {
               levelAndKnobSectionWidth = 35;
               trackGap = 15;
            }

            if (requiredTrackWidthInclcudingGap < (85 * 3) / 2) {
               levelAndKnobSectionWidth = 30;
            }

            if (requiredTrackWidthInclcudingGap < (80 * 3) / 2) {
               sliderSectionWidth = 25;
            }

            if (requiredTrackWidthInclcudingGap < (75 * 3) / 2) {
               sliderSectionWidth = 20;
            }

            if (requiredTrackWidthInclcudingGap < (70 * 3) / 2) {
               trackGap = 5;
               buttonSectionWidth = 30;
            }

            if (requiredTrackWidthInclcudingGap < (65 * 3) / 2) {
               levelAndKnobSectionWidth = 25;
            }

            if (canvasHeight < 160) {
               buttonSectionWidth = Math.min(buttonSectionWidth, 35);
            }
            if (canvasHeight < 140) {
               buttonSectionWidth = Math.min(buttonSectionWidth, 30);
            }
         }

         // normal overrides based on canvas size
         if (levelIndicatorPosition === levelIndicatorPositions.above) {
            if (canvasHeight < 400) {
               levelIndicatorHeight = 110;
            }
            if (canvasHeight < 380) {
               levelIndicatorHeight = 90;
            }
            if (canvasHeight < 360) {
               levelIndicatorHeight = 70;
            }
            if (canvasHeight < 340) {
               verticalElementPadding = 10;
            }
            if (canvasHeight < 320) {
               levelIndicatorHeight = 60;
            }
            if (canvasHeight < 300) {
               levelIndicatorHeight = 50;
            }

            const visibleTracks = Math.min(instrumentCount, 8);
            const requiredTrackWidthInclcudingGap = canvasWidth / visibleTracks;

            if (requiredTrackWidthInclcudingGap < 60 * 2) {
               trackGap = 15;
            }
            if (requiredTrackWidthInclcudingGap < 55 * 2) {
               controlWidth = 35;
            }
            if (requiredTrackWidthInclcudingGap < 50 * 2) {
               trackGap = 10;
            }
            if (requiredTrackWidthInclcudingGap < 45 * 2) {
               controlWidth = 30;
            }
            if (requiredTrackWidthInclcudingGap < 40 * 2) {
               trackGap = 5;
            }
            if (requiredTrackWidthInclcudingGap < 35 * 2) {
               controlWidth = 25;
            }

            sliderSectionWidth = controlWidth;
            levelAndKnobSectionWidth = controlWidth;
            buttonSectionWidth = controlWidth;
            trackWidth =
               sliderSectionWidth + buttonSectionWidth + trackInnerGap;
         }

         // reduce pan knob padding on smaller screens
         if (levelAndKnobSectionWidth <= 35) {
            panKnobPadding = 2;
         }
         if (levelAndKnobSectionWidth <= 30) {
            panKnobPadding = 1;
         }

         // Regular Layout
         let routingButtonWidth = buttonSectionWidth - 2 * panKnobPadding;
         let routingButtonHeight = routingButtonWidth;
         //  const levelIndicatorWidth = levelAndKnobSectionWidth - 20;
         let knobSectionTop =
            svgPadding + levelIndicatorHeight + verticalElementPadding;
         let knobSectionHeight = levelAndKnobSectionWidth;
         const sliderTrackWidth =
            sliderSectionWidth / 2 - sliderSectionWidth / 10;
         let sliderSectionTop =
            svgPadding + levelIndicatorHeight + verticalElementPadding;
         let sliderHeight = canvasHeight - svgPadding - sliderSectionTop;

         let sliderLeftOffset = 0;
         let panKnobLeftOffset = sliderSectionWidth + trackInnerGap;
         let buttonSectionLeftOffset = panKnobLeftOffset;

         let routingButtonY =
            canvasHeight - svgPadding / 2 - routingButtonHeight;

         let fxButtonY = routingButtonY - buttonSectionWidth;
         let buttonSectionTop = fxButtonY - 2 * buttonSectionWidth;

         // Adjacent layout
         if (levelIndicatorPosition === levelIndicatorPositions.adjacent) {
            panKnobLeftOffset = 0;
            routingButtonWidth = routingButtonHeight =
               buttonSectionWidth - 2 * panKnobPadding;
            knobSectionHeight = levelAndKnobSectionWidth;
            trackWidth =
               levelAndKnobSectionWidth +
               trackInnerGap +
               sliderSectionWidth +
               trackInnerGap +
               buttonSectionWidth;
            levelIndicatorHeight =
               canvasHeight -
               2 * svgPadding -
               knobSectionHeight -
               verticalElementPadding;

            knobSectionTop =
               svgPadding + levelIndicatorHeight + verticalElementPadding;
            sliderSectionTop = svgPadding;
            sliderLeftOffset = levelAndKnobSectionWidth + trackInnerGap;
            sliderHeight = canvasHeight - 2 * svgPadding;
            buttonSectionLeftOffset =
               sliderLeftOffset + sliderSectionWidth + trackInnerGap;

            routingButtonY =
               canvasHeight - svgPadding / 2 - routingButtonHeight;

            levelIndicatorWidth = levelAndKnobSectionWidth - 20;
         }

         const muteSoloTop = sliderSectionTop + verticalElementPadding;

         const mixerWidth =
            trackWidth * instrumentCount +
            trackGap * (instrumentCount - 1) +
            2 * svgPadding;

         return {
            controlWidth,
            levelAndKnobSectionWidth,
            sliderSectionWidth,
            trackWidth,
            trackGap,
            panKnobPadding,
            svgPadding,
            levelIndicatorHeight,
            levelIndicatorWidth,
            knobSectionTop,
            knobSectionHeight,
            panKnobLeftOffset,
            sliderHeight,
            sliderTrackWidth,
            sliderSectionTop,
            sliderLeftOffset,
            buttonSectionTop,
            buttonSectionWidth,
            buttonSectionLeftOffset,
            mixerWidth,
            fxButtonY,
            routingButtonWidth,
            routingButtonHeight,
            routingButtonY,
            muteSoloTop,
         };
      }, [canvasHeight, canvasWidth, instrumentCount]);

      const {
         levelAndKnobSectionWidth,
         sliderSectionWidth,
         trackWidth,
         trackGap,
         panKnobPadding,
         svgPadding,
         levelIndicatorHeight,
         knobSectionTop,
         panKnobLeftOffset,
         sliderHeight,
         sliderTrackWidth,
         sliderSectionTop,
         sliderLeftOffset,
         buttonSectionTop,
         buttonSectionWidth,
         buttonSectionLeftOffset,
         mixerWidth,
         fxButtonY,
         routingButtonWidth,
         routingButtonHeight,
         routingButtonY,
      } = mixerSizes;

      if (canvasWidth <= 0 || canvasHeight <= 0) {
         return <MixerWrapper id="mixer-canvas" />;
      }

      return (
         <MixerWrapper id="mixer-canvas">
            {/* <button
            type="button"
            onClick={() =>
               setView({ viewType: sequencerViewTypes.stepSequencer })
            }
         >
            Close Mixer
         </button> */}

            <MixerStyles
               height={canvasHeight}
               width={mixerWidth}
               onContextMenu={(e) => e.preventDefault()}
               className={classNames({
                  greyscale: !compareAnswer && referenceMixer,
               })}
            >
               <LevelsDisplay
                  animate={compareAnswer || !referenceMixer}
                  displayReference={compareAnswer && referenceMixer}
                  mixerSizes={mixerSizes}
                  greyscale={!compareAnswer && referenceMixer}
                  playing={loopModePlaying}
                  highlightedClass={
                     highlightedControls.meters
                        ? getHighlightedClassName(highlightedControls.meters)
                        : null
                  }
               />
               <ControllerStyles
                  compareAnswer={compareAnswer}
                  greyscale={!compareAnswer && referenceMixer}
               >
                  {instrumentRack.map(
                     (
                        { id, name, mixerTrack, referenceMixerTrack, auxTrack },
                        instrumentIndex
                     ) => (
                        <g key={id}>
                           <Knob
                              x={
                                 svgPadding +
                                 panKnobPadding +
                                 panKnobLeftOffset +
                                 (trackWidth + trackGap) * instrumentIndex
                              }
                              y={knobSectionTop + panKnobPadding}
                              highlighted={
                                 highlightedControls[
                                    `track:${instrumentIndex}:pan`
                                 ]
                              }
                              suggestedValue={
                                 suggestedValues[`track:${instrumentIndex}:pan`]
                              }
                              diameter={
                                 levelAndKnobSectionWidth - panKnobPadding * 2
                              }
                              value={
                                 compareAnswer &&
                                 referenceMixerTrack?.gain.value > 0 &&
                                 referenceMixer
                                    ? referenceMixerTrack.pan.value
                                    : mixerTrack.pan.value
                              }
                              incrementCount={mixerTrack.pan.incrementCount}
                              disabled={
                                 compareAnswer || mixerTrack.pan.disabled
                              }
                              ghost={mixerTrack.pan.disabled}
                              tested={
                                 referenceMixerTrack?.gain.value > 0 &&
                                 !mixerTrack.pan.disabled
                              }
                              correct={controllerValuesEqual(
                                 referenceMixerTrack?.pan.value,
                                 mixerTrack.pan.value
                              )}
                              bipolar
                              onChange={(value) =>
                                 setMixerTrackPan(instrumentIndex, value)
                              }
                           />

                           <Slider
                              x={
                                 svgPadding +
                                 sliderLeftOffset +
                                 (trackWidth + trackGap) * instrumentIndex
                              }
                              y={sliderSectionTop}
                              highlighted={
                                 highlightedControls['faders'] ??
                                 highlightedControls[
                                    `track:${instrumentIndex}:gain`
                                 ]
                              }
                              suggestedValue={
                                 suggestedValues[
                                    `track:${instrumentIndex}:gain`
                                 ]
                              }
                              height={sliderHeight}
                              width={sliderSectionWidth}
                              trackWidth={sliderTrackWidth}
                              handleHeight={16}
                              value={
                                 compareAnswer && referenceMixer
                                    ? referenceMixerTrack.gain.value
                                    : mixerTrack.gain.value
                              }
                              incrementCount={mixerTrack.gain.incrementCount}
                              disabled={
                                 compareAnswer || mixerTrack.gain.disabled
                              }
                              ghost={mixerTrack.gain.disabled}
                              tested={!mixerTrack.gain.disabled}
                              correct={controllerValuesEqual(
                                 referenceMixerTrack?.gain.value,
                                 mixerTrack.gain.value
                              )}
                              onChange={(value) =>
                                 setMixerTrackGain(instrumentIndex, value)
                              }
                           />
                           <g className="track-name">
                              <text
                                 x={
                                    svgPadding +
                                    7 +
                                    (trackWidth + trackGap) * instrumentIndex
                                 }
                                 y={levelIndicatorHeight + svgPadding}
                              >
                                 {name}
                              </text>
                           </g>
                           <LedOnButton
                              label="M"
                              value={mixerTrack.muted}
                              x={
                                 svgPadding +
                                 buttonSectionLeftOffset +
                                 panKnobPadding +
                                 (trackWidth + trackGap) * instrumentIndex
                              }
                              y={buttonSectionTop}
                              width={buttonSectionWidth - panKnobPadding * 2}
                              height={buttonSectionWidth * 0.75}
                              onClick={() =>
                                 toggleMixerTrackMute(instrumentIndex)
                              }
                              highlighted={
                                 highlightedControls[
                                    `track:${instrumentIndex}:mute`
                                 ]
                              }
                           />
                           {!auxTrack && (
                              <LedOnButton
                                 label="S"
                                 value={instrumentIndex === soloTrack}
                                 x={
                                    svgPadding +
                                    buttonSectionLeftOffset +
                                    panKnobPadding +
                                    (trackWidth + trackGap) * instrumentIndex
                                 }
                                 //   y={
                                 //      buttonSectionTop +
                                 //      buttonSectionWidth * 0.75 +
                                 //      15
                                 //   }
                                 y={buttonSectionTop + buttonSectionWidth}
                                 width={buttonSectionWidth - panKnobPadding * 2}
                                 height={buttonSectionWidth * 0.75}
                                 onClick={() =>
                                    toggleMixerTrackSolo(instrumentIndex)
                                 }
                                 highlighted={
                                    highlightedControls[
                                       `track:${instrumentIndex}:solo`
                                    ]
                                 }
                              />
                           )}
                           <g
                              className="clickable effects-button text-button"
                              onClick={() =>
                                 setView({
                                    viewType:
                                       sequencerViewTypes.mixerEffectList,
                                    instrumentIndex,
                                 })
                              }
                           >
                              <text
                                 x={
                                    svgPadding +
                                    buttonSectionLeftOffset +
                                    buttonSectionWidth / 2 +
                                    (trackWidth + trackGap) * instrumentIndex
                                 }
                                 y={fxButtonY + buttonSectionWidth / 2}
                                 textAnchor="middle"
                                 dominantBaseline="middle"
                                 fontSize={
                                    buttonSectionWidth <= 30
                                       ? '16px'
                                       : undefined
                                 }
                              >
                                 FX
                              </text>
                           </g>
                           <svg
                              className="clickable icon-button"
                              strokeWidth="0"
                              viewBox="0 0 24 24"
                              height={routingButtonHeight}
                              width={routingButtonWidth}
                              x={
                                 svgPadding +
                                 buttonSectionLeftOffset +
                                 panKnobPadding +
                                 (trackWidth + trackGap) * instrumentIndex
                              }
                              y={routingButtonY}
                              onClick={() =>
                                 setView({
                                    viewType: sequencerViewTypes.mixerRouting,
                                    instrumentIndex,
                                 })
                              }
                           >
                              <rect
                                 fill="transparent"
                                 stroke="none"
                                 x={0}
                                 y={0}
                                 width={24}
                                 height={24}
                              />
                              {/* <path fill="none" d="M0 0h24v24H0z"></path> */}
                              <path d="M19 15.18V7c0-2.21-1.79-4-4-4s-4 1.79-4 4v10c0 1.1-.9 2-2 2s-2-.9-2-2V8.82C8.16 8.4 9 7.3 9 6c0-1.66-1.34-3-3-3S3 4.34 3 6c0 1.3.84 2.4 2 2.82V17c0 2.21 1.79 4 4 4s4-1.79 4-4V7c0-1.1.9-2 2-2s2 .9 2 2v8.18A2.996 2.996 0 0 0 18 21c1.66 0 3-1.34 3-3 0-1.3-.84-2.4-2-2.82zM6 7c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm12 12c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1z"></path>
                              {/* <path d="M19 15.18V7c0-2.21-1.79-4-4-4s-4 1.79-4 4v10c0 1.1-.9 2-2 2s-2-.9-2-2V8.82C8.16 8.4 9 7.3 9 6c0-1.66-1.34-3-3-3S3 4.34 3 6c0 1.3.84 2.4 2 2.82V17c0 2.21 1.79 4 4 4s4-1.79 4-4V7c0-1.1.9-2 2-2s2 .9 2 2v8.18A2.996 2.996 0 0 0 18 21c1.66 0 3-1.34 3-3 0-1.3-.84-2.4-2-2.82z"></path> */}
                           </svg>
                        </g>
                     )
                  )}
               </ControllerStyles>
            </MixerStyles>
         </MixerWrapper>
      );
   }
);

export { FullMixer };
